<template>
  <v-container fluid>
    <h2>{{ $tt('feed.trajectories') }}</h2>
    <p class="ml-2 mb-0">{{ dateFilterRange }}</p>
    <task-filter-component ref="taskFilterComponent" :for-calendar="true" />

    <v-row>
      <v-col cols="12" md="12">
        <v-toolbar flat>
          <!-- カレンダー制御 -->
          <v-btn outlined small @click="setCurrentDateToNow()">今日</v-btn>
          <v-btn outlined small text class="ml-2" @click="$refs.calendar.prev()">
            <v-icon dark>chevron_left</v-icon>
          </v-btn>
          <v-btn outlined small text class="ml-2" @click="$refs.calendar.next()">
            <v-icon dark>chevron_right</v-icon>
          </v-btn>

          <!-- DatePicker -->
          <v-menu :close-on-content-click="false" v-model="showDatePicker">
            <template v-slot:activator="{ on }">
              <v-btn text class="ml-2" v-on="on" x-large>
                {{ currentDateYearMonth }}
                <v-icon>arrow_drop_down</v-icon>
              </v-btn>
            </template>
            <v-date-picker
              v-model="currentDate"
              :min="minStartDateString"
              :max="maxStartDateString"
              :allowed-dates="isAllowedDate"
              @change="showDatePicker = false"
              :day-format="datePickerDayFormat"
            ></v-date-picker>
          </v-menu>
          <div class="flex-grow-1"></div>
          <v-menu bottom right>
            <template v-slot:activator="{ on }">
              <v-btn outlined v-on="on">
                <span>{{ calendarTypeLabel }}</span>
                <v-icon right>mdi-menu-down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="calendarType = 'day'">
                <v-list-item-title>{{ $tt('aid.calendar.day') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="calendarType = 'week'">
                <v-list-item-title>{{ $tt('aid.calendar.week') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="calendarType = 'month'">
                <v-list-item-title>{{ $tt('aid.calendar.month') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="calendarType = '4day'">
                <v-list-item-title>{{ $tt('aid.calendar.fourDays') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-toolbar>

        <!-- カレンダー本体 -->
        <v-sheet elevation="2" height="83vh">
          <v-calendar
            ref="calendar"
            :locale="currentLocale"
            :now="currentDate"
            :type="calendarType"
            v-model="currentDate"
            :events="getTasksAsEvent()"
            :start="currentDate"
            color="primary"
            :event-name="getEventName"
            :event-color="getEventColor"
            :event-text-color="getEventTextColor"
            @click:event="showEvent"
            @click:more="showDay"
            @click:date="showDay"
            :first-interval="3"
            :interval-count="18"
            :interval-minutes="60"
            :interval-height="60"
          />
          <v-menu v-model="showEventPopup" :close-on-content-click="false" :activator="selectedEventElement" offset-x>
            <v-card outlined v-if="currentEvent.task != null">
              <v-toolbar :color="currentEvent.colorStyle.backgroundColor" dense>
                <v-toolbar-title :style="{ color: currentEvent.colorStyle.color }">
                  {{ getCurrentEventLocalizedOperationType() }}
                </v-toolbar-title>
                <div class="flex-grow-1"></div>
                <v-toolbar-items>
                  <v-btn style="background: none" icon>
                    <user-icon-component
                      size="28"
                      :showNickname="true"
                      :iconUrl="getAccount(currentEvent.task).iconUrl"
                      :nickname="getAccount(currentEvent.task).nickname"
                      class="mr-2"
                    />
                  </v-btn>
                  <v-btn icon @click="showDetail(currentEvent.task)" :disabled="hidden(currentEvent.task)">
                    <v-icon :style="{ color: currentEvent.colorStyle.color }">zoom_in</v-icon>
                  </v-btn>
                </v-toolbar-items>
              </v-toolbar>
              <v-list-item two-line>
                <v-list-item-content>
                  <v-list-item-title>{{
                    getFieldNameFromFieldGeoInfoKeyFromTask(currentEvent.task)
                  }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ currentEvent.task | aidTaskRecordEfficiencyDescription }}
                  </v-list-item-subtitle>
                  <v-list-item-title>{{ currentEvent.task | aidTaskRecordDistanceDescription }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-card>
          </v-menu>
        </v-sheet>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import TaskListMixin from '@/components/mixins/task-list-mixin'
import FieldNameMixin from '@/components/mixins/field-name-mixin'
import TaskFilterComponent from '@/components/tasks/TaskFilterComponent'
import UserIconComponent from '@/components/user/UserIconComponent'
import i18n from '@/lib/i18n'
import Operation from '@/lib/operation'
import UserInfo from '@/lib/user-info'
import UTCDateTime from '@/lib/utc-datetime'
import moment from 'moment'

export default {
  name: 'TaskListAsCalendar',
  mixins: [TaskListMixin, FieldNameMixin],
  components: { TaskFilterComponent, UserIconComponent },

  data() {
    return {
      currentDate: null,
      dates: [],
      showDatePicker: false,
      currentEvent: { title: '', colorStyle: { backgroundColor: 'gray', color: 'black' } },
      selectedEventElement: null,
      showEventPopup: false,
      calendarType: 'month',
    }
  },

  methods: {
    showEvent({ nativeEvent, event }) {
      const task = this.currentEvent.task
      // activatorのタイムラグ対策
      const open = () => {
        this.currentEvent = event
        this.selectedEventElement = nativeEvent.target
        setTimeout(() => (this.showEventPopup = true), 10)
      }

      if (this.hidden(event.task)) {
        nativeEvent.stopPropagation()
        return
      }

      if (this.showEventPopup) {
        this.showEventPopup = false
        setTimeout(open, 10)
      } else {
        open()
      }
      nativeEvent.stopPropagation()
    },

    showDay({ date }) {
      this.currentDate = date
      this.calendarType = 'day'
    },

    setCurrentDateToNow() {
      const now = moment()
        .local()
        .format('YYYY-MM-DD')
      this.currentDate = now
    },

    datePickerDayFormat(val) {
      if (val) {
        const str = val.substr(8, 2)
        return Number.parseInt(str)
      }
    },

    getTasksAsEvent() {
      let events = []
      for (let i = 0; i < this.tasks.length; i++) {
        const task = this.tasks[i]
        const startDateTime = UTCDateTime.toUtcMoment(task.startTime).local()
        const startTime = startDateTime.format('HH:mm')
        const endDateTime = UTCDateTime.toUtcMoment(task.endTime).local()
        const title =
          startTime +
          ' ' +
          Operation.getLocalizedName(task.operationType) +
          ' @' +
          this.getFieldNameFromFieldGeoInfoKeyFromTask(task)
        const start = startDateTime.format('YYYY-MM-DD HH:mm')
        const end = endDateTime.format('YYYY-MM-DD HH:mm')
        const colorStyle = Operation.getColor(task.operationType)
        const event = { title, start, end, task, colorStyle }
        events.push(event)
      }
      return events
    },

    getEventName(event) {
      return event.input.title
    },
    getEventColor(event) {
      return event.colorStyle.backgroundColor
    },
    getEventTextColor(event) {
      return event.colorStyle.color
    },

    getCurrentEventLocalizedOperationType() {
      const task = this.currentEvent.task
      if (task) {
        return Operation.getLocalizedName(task.operationType)
      }
    },

    isAllowedDate(val) {
      return this.dates.includes(val)
    },
  },

  computed: {
    calendarTypeLabel() {
      let type = this.calendarType
      if (type === '4day') {
        type = 'fourDays'
      }
      const key = 'aid.calendar.' + type
      return i18n.tt(key)
    },

    currentDateYearMonth() {
      if (!this.currentDate) {
        this.setCurrentDateToNow()
      }
      return UTCDateTime.toYearMonth(this.currentDate)
    },

    currentLocale() {
      return UserInfo.getLanguage()
    },
    minStartDateString() {
      if (this.tasks && this.tasks.length != 0) {
        const startTime = this.tasks[this.tasks.length - 1].startTime
        return UTCDateTime.toUtcMoment(startTime)
          .local()
          .format('YYYY-MM-DD')
      }
      return '2016-01-01'
    },

    maxStartDateString() {
      if (this.tasks && this.tasks.length != 0) {
        const startTime = this.tasks[0].startTime
        return UTCDateTime.toUtcMoment(startTime)
          .local()
          .format('YYYY-MM-DD')
      }
      return this.now
    },
  },
  mounted() {
    const params = {
      filterDateFrom: this.filterDateFrom,
      filterDateTo: this.filterDateTo,
      selectPickerType: this.selectPickerType,
    }
    this.$store.dispatch('setDateFilterRange', params)
  },
  watch: {
    tasks: function(newVal, oldVal) {
      if (newVal && newVal.length >= 1) {
        const startUtc = newVal[0].startTime
        const latestTaskDate = UTCDateTime.toUtcMoment(startUtc)
          .local()
          .format('YYYY-MM-DD')
        this.currentDate = latestTaskDate

        for (let i = 0; i < this.tasks.length; i++) {
          const startTime = this.tasks[i].startTime
          const ymd = UTCDateTime.toUtcMoment(startTime)
            .local()
            .format('YYYY-MM-DD')
          if (!this.dates.includes(ymd)) {
            this.dates.push(ymd)
          }
        }
      }
    },
  },
}
</script>
